import { mdiChevronRight } from '@mdi/js';
import _ from 'lodash';
import React from 'react';
import {
    ProviderFacility,
    ProviderGuarantor,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    SupportFacility,
    SupportGuarantor,
    SupportPayer,
    SupportPayerSubscriber,
    SupportSubscriber,
    SupportSubscriberPatient,
} from '../../../api';
import { LoadingMeter } from '../../base';
import { CardAction } from '../cards/cards';
import { ClaimOverviewSectionMenu } from './claim-overview-section-menu';
import * as S from './claim-overview.styles';
import * as SectionS from '../claim-section.styles';
import {
    formatAddress,
    formatFacilityPhone,
    formatGuarantorName,
    formatPatientDemographics,
    formatPayerDescription,
    formatPayerTitle,
    formatPersonName,
    formatPhone,
    formatPlaceOfService,
    formatRelationshipLabel,
    formatSubscriberSummary,
} from './claim-overview.utils';

const PAYER_ROLES = ['primary', 'secondary', 'tertiary'] as const;
type PayerRole = (typeof PAYER_ROLES)[number];

export type ClaimOverviewSection = 'patient' | PayerRole | 'guarantor' | 'facility';
export type ClaimOverviewSectionActions = Partial<Record<ClaimOverviewSection, CardAction[]>>;

export interface IClaimOverviewProps {
    loading: boolean;
    guarantor?: ProviderGuarantor | SupportGuarantor;
    facility?: ProviderFacility | SupportFacility;
    subscriberPatient?: ProviderSubscriberPatient | SupportSubscriberPatient;
    payerSubscribers?: Array<ProviderPayerSubscriber | SupportPayerSubscriber>;
    payers?: Array<ProviderPayer | SupportPayer>;
    subscribers?: Array<ProviderSubscriber | SupportSubscriber>;
    thirdPartyAdministrators?: Array<ProviderPayer | SupportPayer | undefined>;
    patientDetailsHref?: string;
    viewFullDetailsHref?: string;
    onViewFullDetails?: () => void;
    embedded?: boolean;
    sectionActions?: ClaimOverviewSectionActions;
    /**
     * Payer records are still being fetched in the background. A payer known only by id then reads as loading;
     * once this is false it reads as unavailable — never as a payer with no name (NS-3).
     */
    payersLoading?: boolean;
}

const getPayerAtIndex = (
    index: number,
    payerSubscribers?: Array<ProviderPayerSubscriber | SupportPayerSubscriber>,
    payers?: Array<ProviderPayer | SupportPayer>,
): ProviderPayer | SupportPayer | undefined => {
    const payerSubscriber = _.get(payerSubscribers, index);
    if (!payerSubscriber) {
        return;
    }

    return _.find(payers, { id: _.get(payerSubscriber, 'payer.id') }) || payerSubscriber.payer;
};

export const ClaimOverview: React.FC<IClaimOverviewProps> = ({
    loading,
    guarantor,
    facility,
    subscriberPatient,
    payerSubscribers,
    payers,
    subscribers,
    thirdPartyAdministrators,
    patientDetailsHref,
    viewFullDetailsHref,
    onViewFullDetails,
    embedded = false,
    sectionActions,
    payersLoading = false,
}) => {
    const patient = subscriberPatient?.patient;

    const renderViewFullDetails = (): React.ReactNode => {
        const label = (
            <>
                View full details <S.ChevronIcon path={mdiChevronRight} />
            </>
        );

        if (viewFullDetailsHref) {
            return <S.ViewDetailsLink to={viewFullDetailsHref}>{() => label}</S.ViewDetailsLink>;
        }

        if (onViewFullDetails) {
            return (
                <S.ViewDetailsButton type="button" variant="unstyled" onClick={onViewFullDetails}>
                    {label}
                </S.ViewDetailsButton>
            );
        }

        return null;
    };

    const renderSectionMenu = (section: ClaimOverviewSection): React.ReactNode => {
        const actions = sectionActions?.[section];
        if (!actions?.length) {
            return null;
        }

        return <ClaimOverviewSectionMenu actions={actions} />;
    };

    const renderField = (label: string, value?: React.ReactNode, emphasized?: boolean): React.ReactElement => (
        <S.Field>
            <S.FieldLabel>{label}</S.FieldLabel>
            {value ? <S.FieldValue $emphasized={emphasized}>{value}</S.FieldValue> : <S.EmptyValue>Not on file</S.EmptyValue>}
        </S.Field>
    );

    const renderPatientSection = (): React.ReactElement => {
        const patientName = patient ? formatPersonName(patient.first, patient.last, patient.suffix) : undefined;
        const nameContent = patientName || 'Not on file';

        return (
            <S.Section $first>
                <S.SectionHeader>
                    <S.SectionLabel>Patient</S.SectionLabel>
                    {renderSectionMenu('patient')}
                </S.SectionHeader>
                <S.NameRow>
                    {patientDetailsHref && patientName ? (
                        <S.NameLink to={patientDetailsHref}>{() => nameContent}</S.NameLink>
                    ) : (
                        <S.Name>{nameContent}</S.Name>
                    )}
                </S.NameRow>
                {patient && <S.Demographics>{formatPatientDemographics(patient)}</S.Demographics>}
                {renderField('Patient ID', patient?.externalId || patient?.id)}
                {renderField('Address', formatAddress(patient?.addresses))}
            </S.Section>
        );
    };

    const renderPayerSection = (role: PayerRole, index: number): React.ReactElement => {
        const payerSubscriber = _.get(payerSubscribers, index);
        const payer = getPayerAtIndex(index, payerSubscribers, payers);
        const subscriber = _.find(subscribers, { id: _.get(payerSubscriber, 'subscriber.id') });
        const thirdPartyAdministrator = thirdPartyAdministrators?.[index];
        const hasPayer = Boolean(payerSubscriber && payer);
        const payerDescription = formatPayerDescription(payer);

        return (
            <S.Section key={role}>
                <S.SectionHeader>
                    <S.RoleBadge $variant={role}>{role}</S.RoleBadge>
                    {renderSectionMenu(role)}
                </S.SectionHeader>
                {hasPayer ? (
                    <>
                        <S.Name>
                            {formatPayerTitle(payer) || (payersLoading ? 'Loading payer…' : 'Payer unavailable')}
                        </S.Name>
                        {payerDescription && <S.Subtitle>{payerDescription}</S.Subtitle>}
                        {renderField(
                            'TPA',
                            (thirdPartyAdministrator || payer)?.name || (payersLoading ? 'Loading…' : 'Unavailable'),
                        )}
                        {renderField('Subscriber', formatSubscriberSummary(subscriber))}
                        {renderField('Member ID', payerSubscriber?.memberId, true)}
                        {renderField('SSN', subscriber?.ssn, true)}
                    </>
                ) : (
                    <S.EmptyValue>Not on file</S.EmptyValue>
                )}
            </S.Section>
        );
    };

    const renderGuarantorSection = (): React.ReactElement => {
        const guarantorName = formatGuarantorName(guarantor);
        const relationship = formatRelationshipLabel(subscriberPatient);
        const phone = formatPhone(guarantor?.phone);

        return (
            <S.Section>
                <S.SectionHeader>
                    <S.RoleBadge $variant="guarantor">Guarantor</S.RoleBadge>
                    {renderSectionMenu('guarantor')}
                </S.SectionHeader>
                {guarantor ? (
                    <>
                        <S.Name>{guarantorName}</S.Name>
                        {relationship && <S.Subtitle>{relationship}</S.Subtitle>}
                        {renderField('Address', formatAddress(guarantor.addresses))}
                        <S.Field>
                            <S.FieldLabel>Phone</S.FieldLabel>
                            {phone ? (
                                <S.FieldValue>{phone}</S.FieldValue>
                            ) : (
                                <S.EmptyValue>No phone on file</S.EmptyValue>
                            )}
                        </S.Field>
                    </>
                ) : (
                    <S.EmptyValue>Not on file</S.EmptyValue>
                )}
            </S.Section>
        );
    };

    const renderFacilitySection = (): React.ReactElement => {
        const phone = formatFacilityPhone(facility);

        return (
            <S.Section>
                <S.SectionHeader>
                    <S.RoleBadge $variant="facility">Facility</S.RoleBadge>
                    {renderSectionMenu('facility')}
                </S.SectionHeader>
                {facility ? (
                    <>
                        <S.Name>{facility.name || 'Not on file'}</S.Name>
                        {facility.description && <S.Subtitle>{facility.description}</S.Subtitle>}
                        {renderField('Facility NPI', facility.npi, true)}
                        {renderField('Place of Service', formatPlaceOfService(facility.placeOfServiceCode))}
                        {renderField('Address', formatAddress(facility.addresses))}
                        <S.Field>
                            <S.FieldLabel>Phone</S.FieldLabel>
                            {phone ? (
                                <S.FieldValue>{phone}</S.FieldValue>
                            ) : (
                                <S.EmptyValue>No phone on file</S.EmptyValue>
                            )}
                        </S.Field>
                    </>
                ) : (
                    <S.EmptyValue>Not on file</S.EmptyValue>
                )}
            </S.Section>
        );
    };

    const body = loading ? (
        <S.Loader>
            <LoadingMeter />
        </S.Loader>
    ) : (
        <S.Content>
            {renderPatientSection()}
            {PAYER_ROLES.map((role, index) => renderPayerSection(role, index))}
            {renderGuarantorSection()}
            {renderFacilitySection()}
        </S.Content>
    );

    if (embedded) {
        return (
            <SectionS.ClaimSectionBlock>
                <SectionS.ClaimSectionHeader>
                    <SectionS.ClaimSectionTitle>Claim Overview</SectionS.ClaimSectionTitle>
                    {renderViewFullDetails()}
                </SectionS.ClaimSectionHeader>
                {body}
            </SectionS.ClaimSectionBlock>
        );
    }

    return (
        <S.StyledTitleCard size="large" title="Claim Overview" caption={renderViewFullDetails()}>
            {body}
        </S.StyledTitleCard>
    );
};
